import { UzAlert, UzButton, UzDialog } from '@utazzo/ui';
import { Mail } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { readRefusal } from '../travel-bookings.api';
import { useResendConfirmation } from '../use-travel-bookings';

/**
 * "Resend confirmation" on a confirmed booking.
 *
 * One question, one button. The dialog exists so the operator sees the exact
 * address the mail is going to before it goes - the commonest reason a
 * confirmation "never arrived" is a typo the guest made on the details page,
 * and a resend to the same wrong address helps nobody. Nothing here edits the
 * booking; a wrong address is a support conversation, not a form field.
 */
export const ResendConfirmationDialog = ({
  open,
  onOpenChange,
  bookingId,
  reference,
  product,
  contactEmail,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  reference: string;
  product: 'FLIGHT' | 'HOTEL';
  contactEmail: string;
  onDone: () => void;
}): React.JSX.Element => {
  const resend = useResendConfirmation();
  const thing = product === 'HOTEL' ? 'voucher' : 'e-ticket';

  const confirm = (): void => {
    resend.mutate(bookingId, {
      onSuccess: () => {
        onOpenChange(false);
        onDone();
      },
      onError: (e) => {
        const r = readRefusal(e);
        toast.warning(r?.message ?? 'Could not resend the confirmation. Try again in a moment.');
        // Not confirmed any more (cancelled since the page loaded), or sent a
        // moment ago by someone else: either way there is nothing to retry.
        if (r?.code === 'NOT_CONFIRMED' || r?.code === 'RESENT_RECENTLY') {
          onOpenChange(false);
          onDone();
        }
      },
    });
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={(next) => {
        if (resend.isPending) return;
        onOpenChange(next);
      }}
      title={`Resend confirmation - ${reference}`}
      description={`Sends the booking confirmation with the ${thing} link again.`}
      size="md"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={resend.isPending}>
            Cancel
          </UzButton>
          <UzButton
            onClick={confirm}
            loading={resend.isPending}
            leftIcon={<Mail className="h-4 w-4" />}
          >
            Send again
          </UzButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
          <div className="text-[11px] font-medium tracking-wide text-zinc-500 uppercase">
            Goes to
          </div>
          <div className="mt-0.5 text-sm font-medium break-all text-zinc-900">{contactEmail}</div>
          <div className="mt-1 text-xs text-zinc-500">
            The address the guest typed on the details page. If it is wrong, the guest needs to be
            contacted another way - it cannot be edited here.
          </div>
        </div>
        <UzAlert
          tone="info"
          title="What the guest receives"
          description="The same confirmation as the first time, from the current templates, on every guest channel that is switched on for it. Only one resend is allowed every five minutes."
        />
      </div>
    </UzDialog>
  );
};
